import * as Popover from "@radix-ui/react-popover";
import {
  DownloadIcon,
  EnvelopeClosedIcon,
  Pencil1Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { LuChevronDown } from "react-icons/lu";
import { ReactNode, useState } from "react";
import { saveAs } from "file-saver";
import {
  Auth0AccountRole,
  CRMVisualizationColumnResponse,
  CRMVisualizationResponse,
  ExportsService,
  OrganizationDataKeyResponse,
} from "client/openapi";
import { Dialog } from "components/Dialog";
import IconBubble, { BubbleTone } from "components/IconBubble";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import Notifications from "util/notifications";
import AddOrEditColumnDialog from "./AddOrEditColumnDialog";
import DeleteColumnDialog from "./DeleteColumnDialog";
import { FormOption, SubjectOption } from "../search/editor";

type Opened = "message" | "edit" | "delete";

/**
 * A column header's Actions: a menu of Message, Edit, Export and Delete,
 * each with its own colored icon, as the person pages' Details card labels
 * its rows. Export downloads at once; the others open their window (Delete
 * asks first). A built-in board's column offers Message and Export only.
 */
export default function ColumnActionsMenu({
  column,
  visualization,
  onColumnUpdated,
  onColumnAdded,
  onColumnDeleted,
  studentKeys,
  parentKeys,
  tutorKeys,
  subjects,
  forms,
  orgId,
  builtIn = false,
}: {
  column: CRMVisualizationColumnResponse;
  visualization: CRMVisualizationResponse;
  onColumnUpdated: (column: CRMVisualizationColumnResponse) => void;
  onColumnAdded: (column: CRMVisualizationColumnResponse) => void;
  onColumnDeleted: (columnId: number) => void;
  studentKeys: OrganizationDataKeyResponse[];
  parentKeys: OrganizationDataKeyResponse[];
  tutorKeys: OrganizationDataKeyResponse[];
  subjects: SubjectOption[];
  forms: FormOption[];
  orgId: number;
  builtIn?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!orgId) {
      return Notifications.error("No organization ID available");
    }

    setIsExporting(true);
    try {
      // The column's own kind of person decides which list to export.
      let data;
      let entityType: string;

      if (column.column_type === Auth0AccountRole.PARENT) {
        entityType = "parents";
        data = await ExportsService.exportParents({
          orgId,
          requestBody: (column.parents_in_column || []).map((p) => p.id),
        });
      } else if (column.column_type === Auth0AccountRole.ORG_TUTOR) {
        entityType = "tutors";
        data = await ExportsService.exportTutors({
          orgId,
          requestBody: (column.tutors_in_column || []).map((t) => t.id),
        });
      } else {
        entityType = "students";
        data = await ExportsService.exportStudents({
          orgId,
          requestBody: (column.students_in_column || []).map((s) => s.id),
        });
      }

      const blob = new Blob([data], { type: "text/csv" });
      const fileName = `${entityType}_column_results.csv`;

      saveAs(blob, fileName);
      Notifications.success(
        `${
          entityType.charAt(0).toUpperCase() + entityType.slice(1)
        } data has been exported successfully!`
      );
    } catch (err) {
      console.error("Export error:", err);
      Notifications.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const items: {
    label: string;
    icon: ReactNode;
    tone: BubbleTone;
    onSelect: () => void;
    disabled?: boolean;
  }[] = [
    {
      label: "Message",
      icon: <EnvelopeClosedIcon />,
      tone: BubbleTone.INDIGO,
      onSelect: () => setOpened("message"),
    },
    ...(builtIn
      ? []
      : [
          {
            label: "Edit",
            icon: <Pencil1Icon />,
            tone: BubbleTone.VIOLET,
            onSelect: () => setOpened("edit"),
          },
        ]),
    {
      label: isExporting ? "Exporting..." : "Export",
      icon: <DownloadIcon />,
      tone: BubbleTone.SKY,
      onSelect: handleExport,
      disabled: isExporting,
    },
    ...(builtIn
      ? []
      : [
          {
            label: "Delete",
            icon: <TrashIcon />,
            tone: BubbleTone.RED,
            onSelect: () => setOpened("delete"),
          },
        ]),
  ];

  const close = () => setOpened(null);

  return (
    <>
      <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
        <Popover.Trigger asChild>
          <button type="button" className="board-column-actions">
            Actions
            <LuChevronDown aria-hidden strokeWidth={2.5} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="people-panel__menu"
            align="start"
            sideOffset={4}
          >
            {items.map(({ label, icon, tone, onSelect, disabled }) => (
              <button
                key={label}
                type="button"
                className="people-panel__menu-item board-column-menu-item"
                disabled={disabled}
                onClick={() => {
                  setMenuOpen(false);
                  onSelect();
                }}
              >
                <IconBubble tone={tone} small>
                  {icon}
                </IconBubble>
                {label}
              </button>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Each window is mounted only while open, so it starts fresh. */}
      <Dialog
        open={opened !== null}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        {opened === "message" && (
          <ComposeMessageDialog
            students={
              column.column_type === Auth0AccountRole.ME
                ? column.students_in_column || []
                : undefined
            }
            parents={
              column.column_type === Auth0AccountRole.PARENT
                ? column.parents_in_column || []
                : undefined
            }
            tutors={
              column.column_type === Auth0AccountRole.ORG_TUTOR
                ? column.tutors_in_column || []
                : undefined
            }
            org_id={orgId}
          />
        )}
        {opened === "edit" && (
          <AddOrEditColumnDialog
            visualization={visualization}
            existingColumn={column}
            onColumnUpdated={onColumnUpdated}
            onColumnAdded={onColumnAdded}
            studentKeys={studentKeys}
            parentKeys={parentKeys}
            tutorKeys={tutorKeys}
            subjects={subjects}
            forms={forms}
            setIsOpen={(open) => {
              if (!open) close();
            }}
          />
        )}
        {opened === "delete" && (
          <DeleteColumnDialog
            column={column}
            onDelete={() => {
              close();
              onColumnDeleted(column.id);
            }}
          />
        )}
      </Dialog>
    </>
  );
}
